import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Phone, CheckCircle, Globe, Award, ChevronRight, Users, Target, Zap, Check, Briefcase, Eye } from 'lucide-react';
import logo from '../new.jpg';
import glovisLogo from '../Brand/Glovis Logo_page-0001.jpg';
import dizzeeLogo from '../Brand/Dizzee Logo_page-0001.jpg';
import carefinLogo from '../Brand/Carefin_page-0001.jpg';
import wildArmyLogo from '../Brand/PHOTO-2025-08-04-14-09-20.jpg';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
   <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden ">
  {/* Modern animated background elements */}
  <div className="absolute inset-0 overflow-hidden">
    {/* Subtle grid texture */}
    <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M0 0h60v1H0zM0 0v60h1V0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
    }}></div>
    
    {/* Modern floating gradient blobs */}
    <div className="absolute top-1/4 -left-10 sm:-left-20 w-60 h-60 sm:w-80 sm:h-80 bg-gradient-to-r from-orange-400/8 to-blue-400/8 rounded-full blur-3xl animate-float-slow"></div>
    <div className="absolute bottom-1/3 -right-10 sm:-right-20 w-72 h-72 sm:w-96 sm:h-96 bg-gradient-to-br from-blue-400/8 to-orange-400/8 rounded-full blur-3xl animate-float-medium"></div>
    <div className="absolute top-1/3 right-1/4 w-48 h-48 sm:w-64 sm:h-64 bg-gradient-to-br from-blue-900/4 to-orange-600/4 rounded-full blur-2xl animate-float-fast"></div>
  </div>

  {/* Main content container */}
  <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
    <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
      
      {/* Left content - Text and CTA */}
      <div className="space-y-6 sm:space-y-8 lg:space-y-10 text-left">
        {/* Modern animated badge */}
        <div className="inline-flex items-center px-4 py-2.5 bg-white/90 backdrop-blur-md rounded-full border border-gray-200/50 shadow-lg animate-fade-in mt-8 sm:mt-12 lg:mt-16">
          <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
          <span className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent">
            Since 2018 • Global Presence
          </span>
        </div>
        
        {/* Modern headline with better typography */}
        <div className="space-y-4 sm:space-y-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-left">
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-orange-600 via-blue-700 to-blue-900 bg-clip-text text-transparent">
                PANAGIA
              </span>
              <span className="absolute bottom-2 left-0 w-full h-4 bg-gradient-to-r from-orange-500/30 via-blue-700/30 to-blue-900/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
            </span>{' '}
            <span className="relative inline-block mt-4">
              <span className="relative z-10 bg-gradient-to-r from-orange-600 via-blue-700 to-blue-900 bg-clip-text text-transparent">
                GROUP
              </span>
              <span className="absolute bottom-2 left-0 w-full h-4 bg-gradient-to-r from-orange-500/30 via-blue-700/30 to-blue-900/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
            </span>
          </h1>
          
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-to-r from-orange-500 via-blue-700 to-blue-900 rounded-full mx-0 transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 leading-relaxed max-w-2xl mx-0">
            Redefining Excellence In <span className="font-semibold text-blue-900">Global Commerce</span> and <span className="font-semibold text-orange-600">Strategic Partnerships</span> across industries
          </p>
        </div>

        {/* Value proposition with modern cards */}
        <div className="space-y-6">
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed max-w-2xl mx-0">
            As a diversified global enterprise, Panagia Group delivers innovative solutions across multiple industries, connecting markets and creating value through strategic vision and operational excellence.
          </p>
          
          {/* Modern key differentiators - Responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-lg mx-0 lg:max-w-none">
            {[
              {
                icon: <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />,
                title: "Global Network",
                desc: "Established worldwide presence",
                color: "blue"
              },
              {
                icon: <Users className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />,
                title: "Strategic Partnerships",
                desc: "Collaborative business approach",
                color: "orange"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={`p-3 sm:p-4 rounded-xl border border-gray-200/50 bg-white/70 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:border-${item.color}-200 hover:translate-y-[-2px] hover:bg-white/90`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg bg-${item.color}-100/80 flex-shrink-0`}>
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modern CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-start">
          <Link to="/brand" className="px-6 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px] hover:from-blue-700 hover:to-blue-800 inline-flex items-center justify-center text-sm sm:text-base">
            Explore Our Services
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
          <Link to="/contact" className="px-6 py-3.5 bg-white/90 backdrop-blur-sm text-gray-800 border border-gray-300/50 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 hover:translate-y-[-2px] hover:bg-white inline-flex items-center justify-center text-sm sm:text-base">
            Contact Our Team
            <Phone className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>

              {/* Right content - Modern visual showcase */}
      <div className="relative h-full flex items-center justify-center mt-8 lg:mt-0">
        {/* Modern floating card with logo */}
        <div className="relative w-full max-w-sm sm:max-w-md aspect-square bg-white/95 backdrop-blur-md rounded-3xl shadow-xl border border-gray-100/50 overflow-hidden p-6 sm:p-8">
          {/* Logo container */}
          <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8">
            <div className="relative w-full h-full">
              {/* Logo with fallback */}
              <img 
                src={logo} 
                alt="PANAGIA GROUP Logo" 
                className="h-full w-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden flex-col items-center justify-center h-full text-center">
                <div className="text-4xl sm:text-5xl font-bold text-gray-800 mb-2">PG</div>
                <div className="text-base sm:text-lg font-light tracking-widest text-gray-600">PANAGIA GROUP</div>
              </div>
            </div>
          </div>
          
          {/* Modern decorative elements */}
          {/* <div className="absolute -top-3 -left-3 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-400/20 to-blue-600/20 rounded-full blur-sm animate-pulse"></div>
          <div className="absolute -top-3 -right-3 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-400/20 to-orange-600/20 rounded-full blur-sm animate-pulse delay-500"></div>
          <div className="absolute -bottom-3 -left-3 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500/20 to-blue-700/20 rounded-full blur-sm animate-pulse delay-1000"></div>
          <div className="absolute -bottom-3 -right-3 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-500/20 to-orange-700/20 rounded-full blur-sm animate-pulse delay-1500"></div> */}
        </div>
        
        {/* Modern perfect orange shapes positioned at the bottom of the logo image */}
        <div className="absolute bottom-0 sm:bottom-0 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* HOME CARE */}
            <div className="relative group">
              <div className="bg-gradient-to-r from-orange-500 via-orange-500 to-orange-600 rounded-full w-28 sm:w-32 h-10 sm:h-10 shadow-xl border border-orange-400/40 backdrop-blur-md flex items-center justify-center transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl">
                <div className="text-white text-xs font-bold tracking-wider text-center">HOME CARE</div>
              </div>
              <div className="absolute -top-2 -left-2 w-3 h-3 bg-orange-400/50 rounded-full blur-sm animate-pulse"></div>
              <div className="absolute -top-2 -right-2 w-3 h-3 bg-orange-400/50 rounded-full blur-sm animate-pulse delay-500"></div>
              <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-orange-300/60 rounded-full blur-sm animate-pulse delay-1000"></div>
              <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-300/60 rounded-full blur-sm animate-pulse delay-1500"></div>
            </div>
            
            {/* PERSONAL CARE */}
            <div className="relative group">
              <div className="bg-gradient-to-r from-orange-500 via-orange-500 to-orange-600 rounded-full w-28 sm:w-32 h-10 sm:h-10 shadow-xl border border-orange-400/40 backdrop-blur-md flex items-center justify-center transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl">
                <div className="text-white text-xs font-bold tracking-wider text-center">PERSONAL CARE</div>
              </div>
              <div className="absolute -top-2 -left-2 w-3 h-3 bg-orange-400/50 rounded-full blur-sm animate-pulse delay-300"></div>
              <div className="absolute -top-2 -right-2 w-3 h-3 bg-orange-400/50 rounded-full blur-sm animate-pulse delay-800"></div>
              <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-orange-300/60 rounded-full blur-sm animate-pulse delay-1300"></div>
              <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-300/60 rounded-full blur-sm animate-pulse delay-1800"></div>
            </div>
            
            {/* HYGIENE */}
            <div className="relative group">
              <div className="bg-gradient-to-r from-orange-500 via-orange-500 to-orange-600 rounded-full w-28 sm:w-32 h-10 sm:h-10 shadow-xl border border-orange-400/40 backdrop-blur-md flex items-center justify-center transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl">
                <div className="text-white text-xs font-bold tracking-wider text-center">HYGIENE PRODUCTS</div>
              </div>
              <div className="absolute -top-2 -left-2 w-3 h-3 bg-orange-400/50 rounded-full blur-sm animate-pulse delay-600"></div>
              <div className="absolute -top-2 -right-2 w-3 h-3 bg-orange-400/50 rounded-full blur-sm animate-pulse delay-1100"></div>
              <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-orange-300/60 rounded-full blur-sm animate-pulse delay-1600"></div>
              <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-300/60 rounded-full blur-sm animate-pulse delay-2100"></div>
            </div>
          </div>
        </div>
        
        {/* Modern floating decorative elements */}
        <div className="absolute -z-10 w-48 h-48 sm:w-64 sm:h-64 bg-blue-100/20 rounded-full blur-2xl top-1/4 left-1/4 animate-pulse"></div>
        <div className="absolute -z-10 w-56 h-56 sm:w-72 sm:h-72 bg-orange-100/20 rounded-full blur-2xl bottom-1/4 right-1/4 animate-pulse delay-1000"></div>
      </div>
    </div>
  </div>

  {/* Modern scroll indicator - Hidden on mobile */}
  <div className="hidden sm:block absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
    <div className="flex flex-col items-center">
      <div className="w-4 h-6 sm:w-5 sm:h-8 border-2 border-gray-400/50 rounded-full flex justify-center bg-white/50 backdrop-blur-sm">
        <div className="w-1 h-2 bg-gray-500 rounded-full mt-1 animate-scroll-indicator"></div>
      </div>
      <span className="text-xs text-gray-500 font-medium tracking-wider mt-2">SCROLL TO EXPLORE</span>
    </div>
  </div>
</section>

      {/* Enhanced CSS animations */}
      <style jsx>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 15s ease infinite;
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) translateX(0) rotate(0deg); }
          25% { transform: translateY(-15px) translateX(8px) rotate(1deg); }
          50% { transform: translateY(-8px) translateX(12px) rotate(0deg); }
          75% { transform: translateY(-20px) translateX(4px) rotate(-1deg); }
        }
        
        @keyframes float-medium {
          0%, 100% { transform: translateY(0) translateX(0) rotate(0deg); }
          25% { transform: translateY(12px) translateX(-10px) rotate(-1deg); }
          50% { transform: translateY(6px) translateX(-15px) rotate(0deg); }
          75% { transform: translateY(18px) translateX(-6px) rotate(1deg); }
        }
        
        @keyframes float-fast {
          0%, 100% { transform: translateY(0) translateX(0) scale(1); }
          50% { transform: translateY(-8px) translateX(6px) scale(1.05); }
        }
        
        .animate-float-slow { 
          animation: float-slow 12s ease-in-out infinite; 
        }
        .animate-float-medium { 
          animation: float-medium 8s ease-in-out infinite; 
        }
        .animate-float-fast { 
          animation: float-fast 6s ease-in-out infinite; 
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.05; }
          50% { opacity: 0.15; }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }
        
        @keyframes fade-in-up {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-scroll-indicator {
          animation: scroll-indicator 2s ease-in-out infinite;
        }
        
        @keyframes scroll-indicator {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(8px); opacity: 1; }
        }
        
        .animation-delay-500 {
          animation-delay: 0.5s;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-1500 {
          animation-delay: 1.5s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-3000 {
          animation-delay: 3s;
        }
      `}</style>



      {/* About Section */}
<section className="relative overflow-hidden bg-white">
  {/* Animated gradient background */}
  <div className="absolute inset-0 overflow-hidden opacity-10 pointer-events-none">
    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/20 via-transparent to-transparent animate-pulse-slow"></div>
    <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-orange-400/20 via-transparent to-transparent animate-pulse-slow animation-delay-2000"></div>
  </div>

  {/* Floating particles */}
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <div 
        key={i}
        className="absolute rounded-full bg-blue-500/10"
        style={{
          width: `${Math.random() * 10 + 2}px`,
          height: `${Math.random() * 10 + 2}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animation: `float ${Math.random() * 10 + 10}s linear infinite`,
          animationDelay: `${Math.random() * 5}s`
        }}
      ></div>
    ))}
  </div>

  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative z-10">
          {/* Hero section with animated gradient text */}
      <div className="mb-8 group">
              <div className="w-full max-w-7xl mb-8">
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
          <span className="relative inline-block">
            <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-orange-500 animate-gradient-x bg-[length:200%]">
              Redefining
            </span>
          </span>{' '}
          <span className="relative inline-block mt-4">
            <span className="relative z-10">Global Commerce</span>
            <span className="absolute bottom-2 left-0 w-full h-4 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
          </span>
        </h1>
        <div className="w-24 h-2 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full mb-8 transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
        <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
          Panagia Group delivers premium products through <span className="font-semibold bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">Innovative Supply Chain Solutions</span> across multiple industries worldwide.
        </p>
       
      </div>
    </div>

          {/* About section with modern cards */}
              <div className="grid lg:grid-cols-2 gap-8 items-center mb-8">
              <div className="space-y-4">
        <div>
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 text-sm font-medium mb-4 border border-blue-200/50 shadow-sm">
            ABOUT US
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 leading-tight">
            <span className="relative inline-block">
              <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-orange-500">Redefining Excellence</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
            </span>{' '}
            <span className="relative inline-block mt-4">
              <span className="relative z-10">In Global Trade</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
            </span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full mb-8 transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
        </div>
        
        <div className="space-y-4 text-gray-600 leading-relaxed">
          <p className="text-sm leading-relaxed">
At Panagia group, we are redefining excellence in global trade by delivering
superior-quality products across diverse industries. Established in 2018, we
have emerged as a dynamic conglomerate with a strong foothold in
aviation trade, FMCG, personal care, luxury fragrances, and apparel
manufacturing. Our unwavering commitment to innovation, quality, and
strategic expansion has positioned us as a trusted name in both domestic
and international markets .
With an extensive manufacturing, marketing, and distribution network, we
bring world-class products under renowned brands such as Glovis, Dizee
Wash, Carefin, Wild Army, Cartor Paris, Torts, Bavarian, and Labels of Mind.
Our expertise in supply chain management, product development, and
market penetration ensures seamless business operations for our partners.
Guided by a vision for excellence, Panagia group is committed to forging
long-term, mutually beneficial partnerships with super stockists,
distributors, and retailers, driving growth and profitability across all sectors
we operate in.
          </p>
          
          <div className="grid sm:grid-cols-2 gap-6 pt-4">
            <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                </svg>
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Global Reach</h4>
              <p className="text-gray-600 text-sm">Operating in 3+ countries Across  Continents </p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Quality Assurance</h4>
              <p className="text-gray-600 text-sm">Rigorous standards across all product lines</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="relative h-full">
        <div className="relative z-10 h-full bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100 flex flex-col">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-10 text-white flex-1">
            <div className="flex items-start mb-10">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mr-5 backdrop-blur-sm">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">Our Global Presence</h3>
                <p className="text-blue-200">Expanding since 2018 across continents</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6 mb-10">
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all">
                <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">3+</div>
                <div className="text-blue-200 text-sm">Countries</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all">
                <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">5</div>
                <div className="text-blue-200 text-sm">Core Industries</div>
              </div>
            </div>
            
            <div className="space-y-4">
              {[
                { label: "Aviation Components", scope: "Global", color: "bg-emerald-400" },
                { label: "FMCG Distribution", scope: "3 Countries", color: "bg-cyan-400" },
                { label: "Luxury Fragrances", scope: "India", color: "bg-purple-400" },
                { label: "Apparel ", scope: "3 Countries", color: "bg-amber-400" }
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-center p-4 bg-white/5 rounded-lg backdrop-blur-sm border border-white/5 hover:border-white/10 transition-all">
                  <div className={`w-3 h-3 ${item.color} rounded-full mr-4 animate-pulse`}></div>
                  <span className="flex-1">{item.label}</span>
                  <span className="font-medium text-blue-100">{item.scope}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* <div className="p-8 bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mr-5 shadow-sm">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-gray-800 text-lg">1,200+ Professionals</h4>
                <p className="text-gray-500">Dedicated team driving innovation worldwide</p>
              </div>
            </div>
          </div> */}
        </div>
        
        <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-orange-400 rounded-full opacity-10 blur-3xl animate-pulse-slow"></div>
        <div className="absolute -top-6 -right-6 w-40 h-40 bg-blue-400 rounded-full opacity-10 blur-3xl animate-pulse-slow animation-delay-2000"></div>
      </div>
    </div>

  </div>

  <style jsx global>{`
    @keyframes float {
      0% { transform: translateY(0) translateX(0); }
      50% { transform: translateY(-20px) translateX(10px); }
      100% { transform: translateY(0) translateX(0); }
    }
    @keyframes pulse-slow {
      0%, 100% { opacity: 0.1; }
      50% { opacity: 0.15; }
    }
    .animate-pulse-slow {
      animation: pulse-slow 6s ease-in-out infinite;
    }
    .animation-delay-2000 {
      animation-delay: 2s;
    }
    .animate-gradient-x {
      animation: gradient-x 8s ease infinite;
      background-size: 200% 200%;
    }
    @keyframes gradient-x {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `}</style>
</section>

      {/* Why Partner With Us Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/20">
        {/* Modern background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-gradient-to-r from-blue-400/5 to-orange-400/5 rounded-full blur-3xl animate-float-slow"></div>
          <div className="absolute bottom-1/3 -right-20 w-96 h-96 bg-gradient-to-br from-orange-400/5 to-blue-400/5 rounded-full blur-3xl animate-float-medium"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            
            {/* Left Side - Content */}
            <div className="space-y-6">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 shadow-sm">
                <span className="text-sm font-medium bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent">
                  PARTNERSHIP ADVANTAGES
                </span>
              </div>
              
              {/* Main Heading */}
              <div className="space-y-4">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  <span className="relative inline-block">
                    <span className="relative z-10">Why Partner</span>
                    <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
                  </span>{' '}
                  <span className="relative inline-block mt-4">
                    <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-orange-600">With Us?</span>
                    <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
                  </span>
                </h2>
                
                <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-600 rounded-full transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
                
                <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                  Discover the strategic advantages that make Panagia Group the ideal partner for your business growth and success.
                </p>
              </div>

              {/* CTA Button */}
              <div className="pt-2">
                <Link to="/contact" className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-orange-600 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all hover:translate-y-[-2px]">
                  <span>Explore Partnership Opportunities</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right Side - Benefits Grid */}
            <div className="space-y-4">
              {[
                {
                  icon: <Briefcase className="w-5 h-5" />,
                  title: "Exclusive Product Portfolio",
                  description: "High-quality, in-demand products across multiple categories.",
                  accent: "text-blue-600",
                  bgColor: "bg-blue-50"
                },
                {
                  icon: <Target className="w-5 h-5" />,
                  title: "Proven Market Expertise",
                  description: "Strong presence in aviation trade, FMCG, and fashion sectors with established distribution networks.",
                  accent: "text-orange-600",
                  bgColor: "bg-orange-50"
                },
                {
                  icon: <Award className="w-5 h-5" />,
                  title: "Lucrative Margins",
                  description: "Competitive pricing structures ensuring high profitability for partners.",
                  accent: "text-blue-600",
                  bgColor: "bg-blue-50"
                },
                {
                  icon: <Eye className="w-5 h-5" />,
                  title: "Marketing & Sales Support",
                  description: "Dedicated branding, advertising, and promotional campaigns to boost product visibility.",
                  accent: "text-orange-600",
                  bgColor: "bg-orange-50"
                },
                {
                  icon: <Zap className="w-5 h-5" />,
                  title: "Seamless Supply Chain",
                  description: "Efficient logistics ensuring timely delivery and stock availability.",
                  accent: "text-blue-600",
                  bgColor: "bg-blue-50"
                },
                {
                  icon: <Shield className="w-5 h-5" />,
                  title: "Sustainability & Compliance",
                  description: "Environmentally conscious manufacturing practices with all necessary certifications.",
                  accent: "text-orange-600",
                  bgColor: "bg-orange-50"
                }
              ].map((benefit, index) => (
                <div key={index} className="group">
                  <div className={`flex items-start space-x-4 p-4 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300 border border-transparent hover:border-gray-200 ${benefit.bgColor}`}>
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0 group-hover:shadow-md transition-all duration-200 ${benefit.accent}`}>
                      {benefit.icon}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 space-y-2">
                      <h3 className="font-semibold text-gray-900 group-hover:text-gray-800 transition-colors duration-200">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative z-10">
          {/* Section Header */}
          <div className="text-left mb-6">
            <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-blue-100 to-orange-100 text-blue-800 text-xs font-medium mb-3">
              MISSION & VISION
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <span className="relative inline-block">
                <span className="relative z-10">Driving</span>
                <span className="absolute bottom-1 left-0 w-full h-2 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>{' '}
              <span className="relative inline-block mt-2">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-orange-500">Innovation</span>
                <span className="absolute bottom-1 left-0 w-full h-2 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>{' '}
              <span className="relative inline-block mt-2">
                <span className="relative z-10">Forward</span>
                <span className="absolute bottom-1 left-0 w-full h-2 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
          </div>

          {/* Mission & Vision Cards */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Mission Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl transform group-hover:scale-105 transition-all duration-500"></div>
              <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-500">
                {/* Card Header */}
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">MISSION</h3>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full"></div>
                  </div>
                </div>

                {/* Mission Content */}
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed text-sm">
                    To redefine industry standards by delivering premium-quality products that combine innovation, affordability, and sustainability.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Strategic Alliances</h4>
                        <p className="text-gray-600 text-xs">Building powerful partnerships for mutual growth</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Unwavering Commitment</h4>
                        <p className="text-gray-600 text-xs">Excellence, customer satisfaction, and operational efficiency</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Unparalleled Value</h4>
                        <p className="text-gray-600 text-xs">Creating exceptional value for partners and end consumers</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vision Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl transform group-hover:scale-105 transition-all duration-500"></div>
              <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-500">
                {/* Card Header */}
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">VISION</h3>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
                  </div>
                </div>

                {/* Vision Content */}
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed text-sm">
                    To be a global leader in multi-sector trade, pioneering groundbreaking advancements in aviation, FMCG, personal care, and fashion.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Zap className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Empower Businesses</h4>
                        <p className="text-gray-600 text-xs">Transforming industries and enriching lives</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Award className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Set New Benchmarks</h4>
                        <p className="text-gray-600 text-xs">Quality, trust, and innovation across every market</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Globe className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Global Leadership</h4>
                        <p className="text-gray-600 text-xs">Multi-sector trade excellence worldwide</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-6">
            <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 shadow-md border border-gray-100">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-gray-700 font-medium text-sm">Committed to Excellence Since 2018</span>
            </div>
          </div>
        </div>
      </section>

      {/* Brands and Network Section */}
      <section className="py-8 sm:py-12 lg:py-16 xl:py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-4 sm:mb-6 lg:mb-8">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-50 text-blue-800 text-sm font-medium mb-6 border border-blue-200/50 shadow-sm">
              OUR PORTFOLIO
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              <span className="relative inline-block">
                <span className="relative z-10">Our</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>{' '}
              <span className="relative inline-block mt-4">
                <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-orange-500">Renowned Brands</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full mb-8 transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
              With an extensive manufacturing, marketing, and distribution network, we bring world-class products under renowned brands that consumers trust worldwide.
            </p>
          </div>

          {/* Brands Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 sm:mb-12 lg:mb-16">
            {[
              { 
                name: 'Glovis', 
                route: 'glovis',
                image: glovisLogo,
                description: 'Premium detergent liquid, floor cleaner, toilet cleaner.',
                category: 'HOUSEHOLD & HYGIENE',
                markets: 'Premium Quality',
                hasNavigation: true
              },
              { 
                name: 'Dizzee Wash', 
                route: 'dizzee',
                image: dizzeeLogo,
                description: 'High-performance dishwash liquid, hand wash.',
                category: 'HOUSEHOLD & HYGIENE',
                markets: 'High Performance',
                hasNavigation: true
              },
              { 
                name: 'Carefin', 
                route: 'carefin',
                image: carefinLogo,
                description: 'Sanitary napkins and luxury women\'s perfumes.',
                category: 'FEMININE PERSONAL CARE',
                markets: 'Luxury Range',
                hasNavigation: true
              },
              { 
                name: 'Wild Army', 
                route: 'wild-army',
                image: wildArmyLogo,
                description: 'Signature men\'s perfume line.',
 category: 'MEN\'S PERFUMES',
                markets: 'Signature Collection',
                hasNavigation: false,
                comingSoon: true
              }
            ].map((brand, index) => (
              <div key={index} className="group relative h-full">
                <div 
                  className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 overflow-hidden relative h-full flex flex-col ${brand.hasNavigation ? 'cursor-pointer' : ''}`}
                  onClick={brand.hasNavigation ? () => window.location.href = `/brand/${brand.route}` : undefined}
                >
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  
                  {/* Brand image container */}
                  <div className="relative mb-6 flex-shrink-0">
                    <div className="h-32 w-full rounded-xl flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-white border border-gray-100 relative overflow-hidden">
                      {/* Animated background pattern */}
                      <div className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity duration-300" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                      }}></div>
                      
                      <img 
                        src={brand.image} 
                        alt={`${brand.name} Logo`}
                        className="h-full w-full object-contain max-h-20 rounded-lg group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden items-center justify-center h-full w-full">
                        <span className="text-base font-semibold text-gray-700 text-center w-full">{brand.name}</span>
                      </div>
                    </div>
                    
                    {/* Category badge */}
                    <div className="absolute -top-2 left-0 right-0 mx-2">
                      <span className="block w-full px-2 py-1 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-medium shadow-md text-center">
                        {brand.category}
                      </span>
                    </div>
                    
                    {/* Coming Soon tag */}
                    {brand.comingSoon && (
                      <div className="absolute -bottom-2 left-0 right-0 mx-2">
                        <span className="block w-full px-2 py-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-medium shadow-md text-center">
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Brand content */}
                  <div className="space-y-4 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-900 transition-colors duration-300 text-center">
                      {brand.name}
                    </h3>
                    
                    <p className="text-sm text-gray-600 leading-relaxed flex-1 group-hover:text-gray-700 transition-colors duration-300">
                      {brand.description}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 mt-auto">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-gray-500 font-medium">{brand.markets}</span>
                      </div>
                      
                      {/* View Details Button - Only show for brands with navigation */}
                      {brand.hasNavigation && (
                        <Link to={`/brand/${brand.route}`} className="inline-flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-orange-500 text-white text-xs font-medium rounded-full hover:from-blue-700 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg">
                          <span>View Details</span>
                          <ChevronRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                  
                  {/* Hover effect border */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-blue-200 transition-all duration-300 pointer-events-none"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Network Information */}
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 lg:p-8 xl:p-10 border border-gray-100">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 xl:gap-12 items-center">
              <div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-5">
                  Extensive Global Network
                </h3>
                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed mb-3 sm:mb-4 lg:mb-5">
                  Our expertise in supply chain management, product development, and market penetration ensures seamless business operations for our partners.
                </p>
                <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm md:text-base text-gray-700">Manufacturing Excellence</span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm md:text-base text-gray-700">Marketing & Distribution</span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-green-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm md:text-base text-gray-700">Strategic Partnerships</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8">
                <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 md:mb-4">Partnership Focus</h4>
                <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 sm:mb-5 md:mb-6">
                  Guided by a vision for excellence, Panagia Group is committed to forging long-term, mutually beneficial partnerships with super stockists, distributors, and retailers.
                </p>
                <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 text-center">
                  <div>
                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-orange-600">Super</div>
                    <div className="text-xs sm:text-sm text-gray-600">Stockists</div>
                  </div>
                  <div>
                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">Global</div>
                    <div className="text-xs sm:text-sm text-gray-600">Distributors</div>
                  </div>
                  <div>
                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-orange-500">Premium</div>
                    <div className="text-xs sm:text-sm text-gray-600">Retailers</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-400 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 relative z-10">
          {/* Section Header */}
          <div className="text-left mb-6 sm:mb-8 lg:mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-orange-100 text-blue-800 text-sm font-medium mb-6 border border-blue-200/50 shadow-sm">
              GET IN TOUCH
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              <span className="relative inline-block">
                <span className="relative z-10">Let's</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>{' '}
              <span className="relative inline-block mt-4">
                <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-orange-500">Connect</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>{' '}
              <span className="relative inline-block mt-4">
                <span className="relative z-10">and Grow Together</span>
                <span className="absolute bottom-1 left-0 w-full h-3 bg-gradient-to-r from-blue-500/30 to-orange-400/30 rounded-full transform scale-x-105 group-hover:scale-x-110 origin-left transition-all duration-700 ease-out"></span>
              </span>
            </h2>
            <div className="w-24 h-2 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full mb-8 transform group-hover:scale-x-110 origin-left transition-all duration-500"></div>
            <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
              Ready to explore partnership opportunities? Our team is here to discuss how we can create value together across our diverse portfolio of industries.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 xl:gap-12">
            {/* Contact Information */}
            <div className="space-y-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Contact Information</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Reach out to our dedicated team for partnership inquiries, product information, or any questions about our global operations.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-4">
                {/* Email Contact */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Email Us</h4>
                      <p className="text-gray-600 text-sm mb-3">For general inquiries and partnerships</p>
                      <div className="space-y-1">
                        <a href="mailto:info@panagiaglobal.com" className="text-blue-600 hover:text-blue-700 font-medium text-sm block">
                          info@panagiaglobal.com
                        </a>
                       
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone Contact */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-orange-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Call Us</h4>
                      <p className="text-gray-600 text-sm mb-3">Speak with our business development team</p>
                      <a href="tel:+919961362234" className="text-orange-600 hover:text-orange-700 font-medium text-sm">
                        +91 9961362234
                      </a>
                    </div>
                  </div>
                </div>

                {/* Manufacturing Location */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-green-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Manufactured by</h4>
                      <p className="text-gray-600 text-sm mb-3">Panagia Hygiene & Cosmetics Co.</p>
                      <p className="text-gray-700 text-sm">
                        Wayanad, Kerala, India - 673579
                      </p>
                    </div>
                  </div>
                </div>

                {/* Marketing Office */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-purple-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Marketed by</h4>
                      <p className="text-gray-600 text-sm mb-3">Panagia Global Traders (Pvt) Ltd.</p>
                      <p className="text-gray-700 text-sm">
                        HSR Layout, Bengaluru, Karnataka, India - 560102
                      </p>
                    </div>
                  </div>
                </div>

                {/* Distribution Office */}
                <div className="border-b border-gray-200 pb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-indigo-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Distributed by</h4>
                      <p className="text-gray-600 text-sm mb-3">Regional Office: Panagia Global Traders (Pvt) Ltd.</p>
                      <p className="text-gray-700 text-sm">
                        Wayanad, Kerala, India - 673579
                      </p>
                    </div>
                  </div>
                </div>

                {/* International Office */}
                <div className="pb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-red-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">International Office</h4>
                      <p className="text-gray-600 text-sm mb-3">12403650 Canada INC.</p>
                      <p className="text-gray-700 text-sm">
                        1001 Finch Avenue West, North York<br />
                        ON M3J 2C7, Canada
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Send us a Message</h3>
                <p className="text-gray-600">Fill out the form below and we'll get back to you within 24 hours.</p>
              </div>

              <form className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your email address"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Enter your company name"
                  />
                </div>

                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-2">
                    Area of Interest *
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select an area of interest</option>
                    <option value="aviation">Aviation Components</option>
                    <option value="fmcg">FMCG Distribution</option>
                    <option value="fragrances">Luxury Fragrances</option>
                    <option value="apparel">Apparel Manufacturing</option>
                    <option value="partnership">Strategic Partnership</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Tell us about your inquiry or partnership interest..."
                  ></textarea>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="newsletter"
                    name="newsletter"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="newsletter" className="text-sm text-gray-600">
                    Subscribe to our newsletter for industry updates and insights
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-orange-500 text-white font-semibold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Send Message
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </div>
            </div>
          </div>

          
        
        </div>
      </section>

    </div>
  );
}
